import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import './Sidebar.css';
import { auth, supabase } from '../../config/supabase';
import {
  IconChevronDown,
  IconLayoutGrid,
  IconList,
  IconRefresh,
  IconShoppingCart,
  IconUsers,
} from './SidebarIcons';

function getUserDisplayParts(user: User): { displayName: string; initials: string } {
  const rawName =
    (user.user_metadata?.full_name as string | undefined) ||
    (user.user_metadata?.name as string | undefined) ||
    user.email?.split('@')[0] ||
    'Usuario';

  const parts = rawName.trim().split(/\s+/).filter(Boolean);
  let displayName: string;
  if (parts.length >= 2) {
    displayName = `${parts[0]} ${parts[parts.length - 1][0]}.`;
  } else {
    displayName = parts[0] ?? 'Usuario';
  }

  let initials: string;
  if (parts.length >= 2) {
    initials = `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  } else {
    initials = rawName.slice(0, 2).toUpperCase();
  }

  return { displayName, initials };
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `bm-nav-link${isActive ? ' active' : ''}`;

const Sidebar: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [initials, setInitials] = useState('');
  const [storeName, setStoreName] = useState<string | null>(null);

  useEffect(() => {
    const loadSession = async () => {
      const { user, error } = await auth.getCurrentUser();
      if (error || !user) return;

      const { displayName: name, initials: userInitials } = getUserDisplayParts(user);
      setDisplayName(name);
      setInitials(userInitials);

      const { data: userData } = await supabase
        .from('users')
        .select('store_id, stores!inner(store_name)')
        .eq('id', user.id)
        .maybeSingle();

      const resolvedStoreName = (userData as { stores?: { store_name?: string } } | null)?.stores
        ?.store_name;
      if (resolvedStoreName) {
        setStoreName(resolvedStoreName);
        return;
      }

      const fallback = user.user_metadata?.store_name as string | undefined;
      if (fallback) setStoreName(fallback);
    };

    loadSession();
  }, []);

  const handleLogout = () => {
    auth.signOut();
  };

  return (
    <aside className="bm-sidebar">
      <div className="bm-sidebar-top">
        <div className="bm-sidebar-header">
          <div className="bm-sidebar-brand">BorisMetrics</div>
          <div className="bm-sidebar-tagline">Analytics de operaciones</div>
        </div>

        {storeName && (
          <div className="bm-store-chip" title={storeName}>
            <span className="bm-store-chip-dot" aria-hidden="true" />
            <span className="bm-store-chip-name">{storeName}</span>
            <IconChevronDown className="bm-store-chip-chevron" />
          </div>
        )}
      </div>

      <nav className="bm-sidebar-nav">
        <div className="bm-nav-section-label">PRINCIPAL</div>
        <NavLink to="/dashboard" className={navLinkClass}>
          <IconLayoutGrid />
          <span>Dashboard general</span>
        </NavLink>
        <NavLink to="/ventas" className={navLinkClass}>
          <IconShoppingCart />
          <span>Ventas</span>
        </NavLink>

        <div className="bm-nav-section-label">OPERACIONES</div>
        <div className="bm-nav-section">
          <NavLink to="/cyd" className={navLinkClass}>
            <IconRefresh />
            <span>Cambios y Devoluciones</span>
          </NavLink>
          <div className="bm-nav-subsection">
            <NavLink to="/operaciones" className={navLinkClass}>
              <IconList />
              <span>Operaciones</span>
            </NavLink>
          </div>
        </div>

        <div className="bm-nav-section-label">CLIENTES</div>
        <NavLink to="/clientes" className={navLinkClass}>
          <IconUsers />
          <span>Clientes</span>
        </NavLink>
      </nav>

      <div className="sb-footer">
        <div className="avatar">{initials || '—'}</div>
        <span className="av-name">{displayName || '…'}</span>
        <button type="button" className="logout-btn" title="Cerrar sesión" onClick={handleLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
